/* ==========================================================================
   Atlas Megastore — theme scripts (vanilla JS, no dependencies)
   Uses Shopify's Ajax Cart API and Section Rendering API.
   ========================================================================== */
(() => {
  'use strict';

  const theme = window.theme || {};
  const strings = theme.strings || {};
  const routes = theme.routes || {};
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');
  const FOCUSABLE = 'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, object, embed, [tabindex]:not([tabindex="-1"]), [contenteditable]';

  /* ---------- Utilities ---------- */
  const debounce = (fn, wait = 300) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  };

  const parseHTML = (text) => new DOMParser().parseFromString(text, 'text/html');

  const sectionInnerHTML = (html, selector = '.shopify-section') => {
    const el = parseHTML(html).querySelector(selector);
    return el ? el.innerHTML : '';
  };

  const getFocusable = (container) =>
    Array.from(container.querySelectorAll(FOCUSABLE)).filter(
      (el) => el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length > 0
    );

  let trapHandler = null;
  const trapFocus = (container, elementToFocus) => {
    removeTrapFocus();
    trapHandler = (event) => {
      if (event.key !== 'Tab') return;
      const els = getFocusable(container);
      if (!els.length) return;
      const first = els[0];
      const last = els[els.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === container)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', trapHandler);
    const target = elementToFocus || getFocusable(container)[0] || container;
    requestAnimationFrame(() => target && target.focus({ preventScroll: true }));
  };
  const removeTrapFocus = () => {
    if (trapHandler) document.removeEventListener('keydown', trapHandler);
    trapHandler = null;
  };

  const scrollLock = {
    count: 0,
    lock() { this.count += 1; document.body.classList.add('overflow-hidden'); },
    unlock() { this.count = Math.max(0, this.count - 1); if (!this.count) document.body.classList.remove('overflow-hidden'); }
  };

  const toast = (message, type = 'info') => {
    const el = document.querySelector('[data-toast]');
    if (!el || !message) return;
    el.textContent = message;
    el.classList.toggle('toast--error', type === 'error');
    el.hidden = false;
    clearTimeout(el._timer);
    el._timer = setTimeout(() => { el.hidden = true; }, 4000);
  };

  const fetchJSON = async (url, options = {}) => {
    const response = await fetch(url, {
      ...options,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest', ...(options.headers || {}) }
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.status) {
      const error = new Error(data.description || data.message || strings.cartError);
      error.data = data;
      throw error;
    }
    return data;
  };

  const withRoot = (path) => `${(routes.root || '/').replace(/\/$/, '')}${path}`;

  window.themeUtils = { debounce, parseHTML, trapFocus, removeTrapFocus, toast, scrollLock };

  /* ---------- Overlay panels: drawers & modals ---------- */
  class OverlayPanel extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-close]')) {
          event.preventDefault();
          this.close();
        }
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          this.close();
        }
      });
    }

    get panel() {
      return this.querySelector('.drawer__panel, .modal__panel, .search-modal__panel') || this;
    }

    open(opener) {
      if (this.classList.contains('is-open')) return;
      this.opener = opener || document.activeElement;
      clearTimeout(this._hideTimer);
      this.hidden = false;
      // Force reflow so the transition runs.
      void this.offsetWidth;
      this.classList.add('is-open');
      if (this.opener && this.opener.setAttribute) this.opener.setAttribute('aria-expanded', 'true');
      scrollLock.lock();
      trapFocus(this.panel, this.initialFocus());
      this.dispatchEvent(new CustomEvent('overlay:open', { bubbles: true }));
    }

    initialFocus() {
      return this.querySelector('[autofocus]') || this.panel;
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      removeTrapFocus();
      scrollLock.unlock();
      if (this.opener && this.opener.setAttribute) {
        this.opener.setAttribute('aria-expanded', 'false');
        if (document.body.contains(this.opener)) this.opener.focus({ preventScroll: true });
      }
      this._hideTimer = setTimeout(() => { this.hidden = true; }, 400);
      this.dispatchEvent(new CustomEvent('overlay:close', { bubbles: true }));
    }
  }
  class SideDrawer extends OverlayPanel {}
  class ModalDialog extends OverlayPanel {}
  customElements.define('side-drawer', SideDrawer);
  customElements.define('modal-dialog', ModalDialog);

  // Any element with data-open="ElementId" opens that drawer / modal.
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-open]');
    if (!opener) return;
    const target = document.getElementById(opener.dataset.open);
    if (target && typeof target.open === 'function') {
      event.preventDefault();
      target.open(opener);
    }
  });

  /* ---------- Mobile menu drawer with sliding sub-panels ---------- */
  class MenuDrawer extends SideDrawer {
    connectedCallback() {
      super.connectedCallback();
      if (this._menuBound) return;
      this._menuBound = true;
      this.stack = [];
      this.addEventListener('click', (event) => {
        const openBtn = event.target.closest('[data-submenu-open]');
        const backBtn = event.target.closest('[data-submenu-back]');
        if (openBtn) this.openSubmenu(openBtn.dataset.submenuOpen, openBtn);
        if (backBtn) this.back();
      });
    }

    openSubmenu(id, trigger) {
      const target = this.querySelector(`#${CSS.escape(id)}`);
      if (!target) return;
      const current = this.stack.length ? this.stack[this.stack.length - 1].panel : this.querySelector('[data-menu-root]');
      current.classList.add('is-behind');
      target.classList.add('is-active');
      target.scrollTop = 0;
      trigger.setAttribute('aria-expanded', 'true');
      this.stack.push({ panel: target, trigger });
      const focusTarget = target.querySelector('[data-submenu-back]');
      if (focusTarget) setTimeout(() => focusTarget.focus(), 50);
    }

    back() {
      const entry = this.stack.pop();
      if (!entry) return;
      entry.panel.classList.remove('is-active');
      entry.trigger.setAttribute('aria-expanded', 'false');
      const previous = this.stack.length ? this.stack[this.stack.length - 1].panel : this.querySelector('[data-menu-root]');
      previous.classList.remove('is-behind');
      setTimeout(() => entry.trigger.focus(), 50);
    }

    close() {
      super.close();
      setTimeout(() => {
        while (this.stack.length) {
          const entry = this.stack.pop();
          entry.panel.classList.remove('is-active');
          entry.trigger.setAttribute('aria-expanded', 'false');
        }
        this.querySelectorAll('.is-behind').forEach((el) => el.classList.remove('is-behind'));
      }, 400);
    }
  }
  customElements.define('menu-drawer', MenuDrawer);

  /* ---------- Search modal ---------- */
  class SearchModal extends OverlayPanel {
    initialFocus() {
      return this.querySelector('input[type="search"]') || this.panel;
    }
  }
  customElements.define('search-modal', SearchModal);

  /* ---------- Sticky header ---------- */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.sticky || 'none';
      this.section = this.closest('.shopify-section') || this;
      if (this.mode !== 'none') this.section.classList.add('site-header-section--sticky');
      this.lastY = window.scrollY;
      this.updateHeight = this.updateHeight.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.updateHeight();
      window.addEventListener('resize', debounce(this.updateHeight, 150));
      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(this.updateHeight);
        this.ro.observe(this);
      }
      if (this.mode !== 'none') window.addEventListener('scroll', this.onScroll, { passive: true });
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      if (this.ro) this.ro.disconnect();
    }

    updateHeight() {
      const height = this.offsetHeight;
      document.documentElement.style.setProperty('--header-height', `${height}px`);
      document.documentElement.style.setProperty('--header-sticky-offset', this.mode === 'none' ? '0px' : `${height}px`);
    }

    onScroll() {
      const y = window.scrollY;
      const top = this.section.offsetTop;
      this.section.classList.toggle('is-scrolled', y > top + 4);
      if (this.mode === 'scroll-up') {
        const menuOpen = this.querySelector('.is-open');
        const goingDown = y > this.lastY && y > top + this.offsetHeight + 60;
        this.section.classList.toggle('is-hidden', goingDown && !menuOpen);
        document.documentElement.style.setProperty('--header-sticky-offset', goingDown ? '0px' : `${this.offsetHeight}px`);
      }
      this.lastY = y;
    }
  }
  customElements.define('sticky-header', StickyHeader);

  /* ---------- Desktop navigation: dropdowns & mega menus ---------- */
  class HeaderMenu extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.items = Array.from(this.querySelectorAll('[data-menu-item]'));
      this.overlay = document.querySelector('[data-header-overlay]');
      this.items.forEach((item) => {
        const button = item.querySelector('[data-menu-toggle]');
        if (!button) return;
        button.addEventListener('click', (event) => {
          event.preventDefault();
          this.isOpen(item) ? this.close(item) : this.open(item);
        });
        item.addEventListener('mouseenter', () => {
          if (!canHover.matches) return;
          clearTimeout(item._closeTimer);
          item._openTimer = setTimeout(() => this.open(item), 90);
        });
        item.addEventListener('mouseleave', () => {
          if (!canHover.matches) return;
          clearTimeout(item._openTimer);
          item._closeTimer = setTimeout(() => this.close(item), 180);
        });
        item.addEventListener('focusout', (event) => {
          if (!item.contains(event.relatedTarget)) this.close(item);
        });
        item.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && this.isOpen(item)) {
            event.stopPropagation();
            this.close(item);
            button.focus();
          }
        });
      });

      // Nested flyouts inside simple dropdowns
      this.querySelectorAll('[data-submenu-toggle]').forEach((btn) => {
        btn.addEventListener('click', (event) => {
          event.preventDefault();
          const sub = document.getElementById(btn.getAttribute('aria-controls'));
          const expanded = btn.getAttribute('aria-expanded') === 'true';
          btn.setAttribute('aria-expanded', String(!expanded));
          if (sub) sub.classList.toggle('is-open', !expanded);
        });
      });

      if (this.overlay) this.overlay.addEventListener('click', () => this.closeAll());

      document.addEventListener('shopify:block:select', (event) => {
        const item = event.target.closest && event.target.closest('[data-menu-item]');
        if (item && this.contains(item)) this.open(item);
      });
      document.addEventListener('shopify:block:deselect', () => this.closeAll());
    }

    isOpen(item) {
      return item.querySelector('[data-menu-toggle]').getAttribute('aria-expanded') === 'true';
    }

    open(item) {
      this.items.forEach((other) => other !== item && this.close(other));
      const button = item.querySelector('[data-menu-toggle]');
      const panel = item.querySelector('[data-menu-panel]');
      if (!button || !panel) return;
      button.setAttribute('aria-expanded', 'true');
      panel.classList.add('is-open');
      item.classList.add('is-open');
      if (panel.classList.contains('mega-menu') && this.overlay) {
        const rect = this.closest('.header-wrapper').getBoundingClientRect();
        this.overlay.style.top = `${Math.max(0, rect.bottom)}px`;
        this.overlay.classList.add('is-visible');
      }
    }

    close(item) {
      const button = item.querySelector('[data-menu-toggle]');
      const panel = item.querySelector('[data-menu-panel]');
      if (!button || !panel) return;
      button.setAttribute('aria-expanded', 'false');
      panel.classList.remove('is-open');
      item.classList.remove('is-open');
      panel.querySelectorAll('[data-submenu-toggle]').forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
      panel.querySelectorAll('.dropdown__sub.is-open').forEach((sub) => sub.classList.remove('is-open'));
      if (!this.querySelector('.mega-menu.is-open') && this.overlay) this.overlay.classList.remove('is-visible');
    }

    closeAll() {
      this.items.forEach((item) => this.close(item));
    }
  }
  customElements.define('header-menu', HeaderMenu);

  /* ---------- Predictive search ---------- */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      this.cache = new Map();
      if (!this.input || !this.results) return;
      this.input.setAttribute('autocomplete', 'off');
      this.input.addEventListener('input', debounce(() => this.onInput(), 280));
      this.input.addEventListener('focus', () => { if (this.input.value.trim() && this.results.innerHTML.trim()) this.openResults(); });
      this.addEventListener('keydown', (event) => this.onKeydown(event));
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) setTimeout(() => this.closeResults(), 100);
      });
      this.addEventListener('click', (event) => {
        const searchAll = event.target.closest('[data-search-all]');
        if (searchAll) {
          event.preventDefault();
          this.querySelector('form').submit();
        }
      });
    }

    get term() { return this.input.value.trim(); }

    async onInput() {
      const term = this.term;
      if (!term.length) { this.closeResults(); return; }
      if (this.cache.has(term)) { this.render(this.cache.get(term)); return; }
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      this.results.innerHTML = '<div class="predictive-search__loading"><span class="spinner" aria-hidden="true"></span><span class="visually-hidden">' + (strings.loading || 'Loading') + '</span></div>';
      this.openResults();
      const params = new URLSearchParams({
        q: term,
        section_id: 'predictive-search',
        'resources[type]': 'product,collection,query,article,page',
        'resources[limit]': '6',
        'resources[limit_scope]': 'each',
        'resources[options][unavailable_products]': 'last'
      });
      try {
        const response = await fetch(`${routes.predictiveSearch}?${params}`, { signal: this.controller.signal });
        if (!response.ok) throw new Error(response.status);
        const html = sectionInnerHTML(await response.text(), '#shopify-section-predictive-search');
        this.cache.set(term, html);
        if (term === this.term) this.render(html);
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.closeResults();
      }
    }

    render(html) {
      this.results.innerHTML = html;
      this.openResults();
      this.selectedIndex = -1;
    }

    openResults() {
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    closeResults() {
      this.results.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
    }

    onKeydown(event) {
      if (event.key === 'Escape' && !this.results.hidden) {
        event.stopPropagation();
        this.closeResults();
        this.input.focus();
        return;
      }
      if (!['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key) || this.results.hidden) return;
      const options = Array.from(this.results.querySelectorAll('[role="option"]'));
      if (!options.length) return;
      if (event.key === 'Enter') {
        const selected = options[this.selectedIndex];
        if (selected) {
          event.preventDefault();
          const link = selected.querySelector('a, button') || selected;
          link.click();
        }
        return;
      }
      event.preventDefault();
      this.selectedIndex = event.key === 'ArrowDown'
        ? (this.selectedIndex + 1) % options.length
        : (this.selectedIndex - 1 + options.length) % options.length;
      options.forEach((opt, i) => opt.setAttribute('aria-selected', String(i === this.selectedIndex)));
      const active = options[this.selectedIndex];
      this.input.setAttribute('aria-activedescendant', active.id);
      active.scrollIntoView({ block: 'nearest' });
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ---------- Cart API ---------- */
  const Cart = {
    sectionIds() {
      const ids = ['cart-drawer'];
      document.querySelectorAll('cart-items[data-section-id]').forEach((el) => {
        if (!ids.includes(el.dataset.sectionId)) ids.push(el.dataset.sectionId);
      });
      return ids;
    },

    async add(formData) {
      formData.append('sections', this.sectionIds().join(','));
      formData.append('sections_url', window.location.pathname);
      const response = await fetch(`${routes.cartAdd}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.status) {
        const error = new Error(data.description || data.message || strings.cartError);
        error.data = data;
        throw error;
      }
      this.render(data.sections);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'add', item: data } }));
      return data;
    },

    async change(key, quantity) {
      const data = await fetchJSON(`${routes.cartChange}.js`, {
        method: 'POST',
        body: JSON.stringify({ id: key, quantity, sections: this.sectionIds(), sections_url: window.location.pathname })
      });
      this.render(data.sections);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'change', cart: data } }));
      return data;
    },

    async updateNote(note) {
      return fetchJSON(`${routes.cartUpdate}.js`, { method: 'POST', body: JSON.stringify({ note }) });
    },

    async refresh() {
      const response = await fetch(`${window.location.pathname}?sections=${this.sectionIds().join(',')}`);
      if (response.ok) this.render(await response.json());
    },

    render(sections) {
      if (!sections) return;
      const active = document.activeElement;
      const activeId = active && active.id;
      Object.entries(sections).forEach(([id, html]) => {
        if (!html) return;
        const doc = parseHTML(html);
        if (id === 'cart-drawer') {
          const fresh = doc.querySelector('[data-cart-drawer-inner]');
          const current = document.querySelector('#CartDrawer [data-cart-drawer-inner]');
          if (fresh && current) current.innerHTML = fresh.innerHTML;
          const countEl = doc.querySelector('[data-cart-count-value]');
          if (countEl) this.updateCount(parseInt(countEl.dataset.cartCountValue, 10) || 0);
        } else {
          const fresh = doc.querySelector('[data-cart-page-inner]');
          const current = document.querySelector(`cart-items[data-section-id="${id}"] [data-cart-page-inner]`);
          if (fresh && current) current.innerHTML = fresh.innerHTML;
        }
      });
      if (activeId) {
        const again = document.getElementById(activeId);
        if (again) again.focus({ preventScroll: true });
      }
    },

    updateCount(count) {
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = count > 99 ? '99+' : String(count);
        el.dataset.count = String(count);
      });
      document.querySelectorAll('[data-cart-count-label]').forEach((el) => {
        el.textContent = el.dataset.template ? el.dataset.template.replace('[count]', count) : count;
      });
    },

    openDrawer(opener) {
      const drawer = document.getElementById('CartDrawer');
      if (drawer && theme.cartType === 'drawer') drawer.open(opener);
      else window.location.href = routes.cart;
    }
  };
  window.themeCart = Cart;

  /* ---------- Quantity input ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.input = this.querySelector('input');
      this.addEventListener('click', (event) => {
        const btn = event.target.closest('[data-qty]');
        if (!btn || !this.input) return;
        event.preventDefault();
        const step = parseInt(this.input.step, 10) || 1;
        const min = parseInt(this.input.min, 10) || 0;
        const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
        const current = parseInt(this.input.value, 10) || 0;
        let next = btn.dataset.qty === 'plus' ? current + step : current - step;
        next = Math.min(Math.max(next, min), max);
        if (next === current) return;
        this.input.value = next;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ---------- Cart line items (drawer + cart page) ---------- */
  class CartItems extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.onQuantityChange = debounce((input) => this.updateLine(input.dataset.key, parseInt(input.value, 10), input), 350);
      this.addEventListener('change', (event) => {
        const input = event.target.closest('[data-cart-quantity]');
        if (input) this.onQuantityChange(input);
        const note = event.target.closest('[data-cart-note]');
        if (note) Cart.updateNote(note.value).catch(() => toast(strings.cartError, 'error'));
      });
      this.addEventListener('click', (event) => {
        const remove = event.target.closest('[data-cart-remove]');
        if (remove) {
          event.preventDefault();
          this.updateLine(remove.dataset.key, 0, remove);
        }
      });
    }

    async updateLine(key, quantity, source) {
      if (Number.isNaN(quantity)) return;
      const line = source.closest('[data-cart-item]');
      if (line) line.classList.add('is-loading');
      try {
        const cart = await Cart.change(key, quantity);
        const item = cart.items.find((i) => i.key === key);
        if (quantity > 0 && item && item.quantity < quantity) {
          const fresh = this.querySelector(`[data-cart-item][data-key="${CSS.escape(key)}"] [data-line-error]`);
          if (fresh) { fresh.textContent = strings.quantityError; fresh.hidden = false; }
        }
      } catch (error) {
        if (line) {
          line.classList.remove('is-loading');
          const errorEl = line.querySelector('[data-line-error]');
          if (errorEl) { errorEl.textContent = error.message; errorEl.hidden = false; }
        }
        toast(error.message || strings.cartError, 'error');
        Cart.refresh();
      }
    }
  }
  customElements.define('cart-items', CartItems);

  /* ---------- Product form (add to cart) ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.submitButton = this.form.querySelector('[type="submit"]');
      this.errorEl = this.querySelector('[data-form-error]');
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
    }

    async onSubmit(event) {
      if (theme.cartType === 'page' && !this.closest('modal-dialog')) return; // native submit to /cart/add
      event.preventDefault();
      if (!this.submitButton || this.submitButton.disabled || this.submitButton.getAttribute('aria-disabled') === 'true') return;
      this.setError();
      this.submitButton.classList.add('is-loading');
      this.submitButton.setAttribute('aria-disabled', 'true');
      try {
        await Cart.add(new FormData(this.form));
        const modal = this.closest('modal-dialog');
        if (modal) modal.close();
        if (theme.cartType === 'page') {
          window.location.href = routes.cart;
          return;
        }
        Cart.openDrawer(this.submitButton);
      } catch (error) {
        this.setError(error.message);
      } finally {
        this.submitButton.classList.remove('is-loading');
        this.submitButton.removeAttribute('aria-disabled');
      }
    }

    setError(message) {
      if (!this.errorEl) { if (message) toast(message, 'error'); return; }
      this.errorEl.hidden = !message;
      const text = this.errorEl.querySelector('[data-form-error-text]');
      if (text) text.textContent = message || '';
    }
  }
  customElements.define('product-form', ProductForm);

  /* ---------- Variant picker ---------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      const dataEl = this.querySelector('[data-variant-json]');
      this.variants = dataEl ? JSON.parse(dataEl.textContent) : [];
      this.section = this.closest('[data-product-section]') || document;
      this.updateUrl = this.dataset.updateUrl === 'true';
      this.addEventListener('change', () => this.onChange());
      this.updateAvailability();
      this.renderInitialInventory();
    }

    renderInitialInventory() {
      const input = this.section.querySelector(`[data-variant-id-input="${this.dataset.sectionId}"]`);
      const variant = input && this.variants.find((v) => String(v.id) === input.value);
      this.section.querySelectorAll('[data-inventory]').forEach((el) => {
        el.innerHTML = variant && variant.inventory_html ? variant.inventory_html : '';
        el.hidden = !el.innerHTML;
      });
    }

    selectedOptions() {
      return Array.from(this.querySelectorAll('[data-option-position]')).map((group) => {
        const checked = group.querySelector('input:checked');
        if (checked) return checked.value;
        const select = group.querySelector('select');
        return select ? select.value : null;
      });
    }

    onChange() {
      const selected = this.selectedOptions();
      this.currentVariant = this.variants.find((v) => v.options.every((opt, i) => opt === selected[i]));
      this.querySelectorAll('[data-option-position]').forEach((group, i) => {
        const label = group.querySelector('[data-selected-value]');
        if (label) label.textContent = selected[i] || '';
      });
      this.updateAvailability();
      this.updateSection();
      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant: this.currentVariant } }));
    }

    updateAvailability() {
      const selected = this.selectedOptions();
      this.querySelectorAll('[data-option-position]').forEach((group, index) => {
        const matching = this.variants.filter((v) => v.available && selected.slice(0, index).every((opt, i) => v.options[i] === opt));
        group.querySelectorAll('input[type="radio"]').forEach((input) => {
          const available = matching.some((v) => v.options[index] === input.value);
          input.classList.toggle('is-unavailable', !available);
        });
        group.querySelectorAll('option').forEach((option) => {
          const available = matching.some((v) => v.options[index] === option.value);
          option.textContent = available ? option.value : `${option.value} — ${strings.unavailable}`;
        });
      });
    }

    updateSection() {
      const variant = this.currentVariant;
      const section = this.section;
      const sectionId = this.dataset.sectionId;

      section.querySelectorAll(`[data-variant-id-input="${sectionId}"]`).forEach((input) => {
        input.value = variant ? variant.id : '';
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });

      // Price
      section.querySelectorAll('[data-price-container]').forEach((el) => {
        if (!variant) return;
        el.innerHTML = this.priceHTML(variant);
      });

      // SKU
      section.querySelectorAll('[data-sku]').forEach((el) => {
        el.hidden = !(variant && variant.sku);
        const value = el.querySelector('[data-sku-value]');
        if (value) value.textContent = variant ? variant.sku : '';
      });

      // Inventory
      section.querySelectorAll('[data-inventory]').forEach((el) => {
        if (!variant || !variant.inventory_html) { el.hidden = true; return; }
        el.hidden = false;
        el.innerHTML = variant.inventory_html;
      });

      // Buttons
      section.querySelectorAll(`[data-add-button="${sectionId}"]`).forEach((btn) => {
        const label = btn.querySelector('[data-add-label]') || btn;
        const available = !!(variant && variant.available);
        btn.disabled = !available;
        if (!variant) label.textContent = strings.unavailable;
        else if (!variant.available) label.textContent = strings.soldOut;
        else label.textContent = btn.dataset.addLabel || strings.addToCart;
      });
      section.querySelectorAll('.shopify-payment-button').forEach((el) => {
        el.hidden = !(variant && variant.available);
      });

      // Media
      if (variant && variant.featured_media_id) {
        const gallery = section.querySelector('product-gallery');
        if (gallery) gallery.goToMedia(variant.featured_media_id);
      }

      // Pickup availability
      const pickup = section.querySelector('pickup-availability');
      if (pickup && variant) pickup.fetchAvailability(variant.id, variant.available);

      // URL
      if (this.updateUrl && variant) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    priceHTML(variant) {
      const onSale = variant.compare_at_price > variant.price;
      let html = `<div class="price price--large${onSale ? ' price--on-sale' : ''}">`;
      html += `<span class="visually-hidden">${onSale ? (this.dataset.salePriceLabel || '') : (this.dataset.priceLabel || '')}</span>`;
      html += `<span class="price__current">${variant.price_html}</span>`;
      if (onSale) {
        html += `<span class="visually-hidden">${this.dataset.regularPriceLabel || ''}</span><s class="price__compare">${variant.compare_at_price_html}</s>`;
        if (variant.save_html) html += `<span class="price__save">${variant.save_html}</span>`;
      }
      if (variant.unit_price_html) html += `<span class="price__unit">${variant.unit_price_html}</span>`;
      html += '</div>';
      return html;
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* ---------- Pickup availability ---------- */
  class PickupAvailability extends HTMLElement {
    fetchAvailability(variantId, available) {
      if (!available) { this.innerHTML = ''; return; }
      const url = withRoot(`/variants/${variantId}/?section_id=pickup-availability`);
      fetch(url)
        .then((r) => r.text())
        .then((text) => {
          const html = sectionInnerHTML(text, '.shopify-section');
          this.innerHTML = html;
        })
        .catch(() => { this.innerHTML = ''; });
    }
    connectedCallback() {
      if (this.dataset.variantId && this.dataset.available === 'true') this.fetchAvailability(this.dataset.variantId, true);
    }
  }
  customElements.define('pickup-availability', PickupAvailability);

  /* ---------- Product gallery ---------- */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.track = this.querySelector('[data-gallery-slides]');
      this.slides = Array.from(this.querySelectorAll('[data-media-id]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));
      this.counter = this.querySelector('[data-gallery-counter]');
      if (!this.track) return;

      this.thumbs.forEach((thumb, index) => {
        thumb.addEventListener('click', () => this.goTo(index));
      });
      this.querySelectorAll('[data-gallery-prev]').forEach((btn) => btn.addEventListener('click', () => this.goTo(this.index - 1)));
      this.querySelectorAll('[data-gallery-next]').forEach((btn) => btn.addEventListener('click', () => this.goTo(this.index + 1)));
      this.track.addEventListener('scroll', debounce(() => this.syncFromScroll(), 80), { passive: true });
      this.querySelectorAll('[data-zoom-index]').forEach((btn) => {
        btn.addEventListener('click', () => this.openLightbox(parseInt(btn.dataset.zoomIndex, 10), btn));
      });
      this.index = 0;
      const initial = this.slides.findIndex((s) => s.classList.contains('is-initial'));
      if (initial > 0) this.goTo(initial, false);
      this.setActive(Math.max(initial, 0));
    }

    goToMedia(mediaId) {
      const index = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
      if (index > -1) this.goTo(index);
    }

    goTo(index, smooth = true) {
      if (!this.slides.length) return;
      const target = (index + this.slides.length) % this.slides.length;
      const slide = this.slides[target];
      if (this.track.scrollWidth > this.track.clientWidth + 2) {
        this.programmatic = true;
        clearTimeout(this.programmaticTimer);
        this.programmaticTimer = setTimeout(() => { this.programmatic = false; }, 700);
        this.track.scrollTo({ left: slide.offsetLeft - this.track.firstElementChild.offsetLeft, behavior: smooth && !prefersReducedMotion.matches ? 'smooth' : 'auto' });
      }
      this.setActive(target);
    }

    syncFromScroll() {
      if (this.programmatic) return;
      const width = this.track.clientWidth;
      if (!width) return;
      const index = Math.round(this.track.scrollLeft / width);
      if (index !== this.index) this.setActive(index);
    }

    setActive(index) {
      this.index = index;
      this.thumbs.forEach((thumb, i) => {
        thumb.setAttribute('aria-current', String(i === index));
      });
      const list = this.querySelector('.product-gallery__thumbs');
      const active = this.thumbs[index];
      if (list && active) {
        // Scroll only the thumbnail strip (scrollIntoView would interrupt the slide scroll).
        const item = active.parentElement;
        if (list.scrollWidth > list.clientWidth) list.scrollTo({ left: item.offsetLeft - list.clientWidth / 2 + item.offsetWidth / 2, behavior: 'smooth' });
        if (list.scrollHeight > list.clientHeight) list.scrollTo({ top: item.offsetTop - list.clientHeight / 2 + item.offsetHeight / 2, behavior: 'smooth' });
      }
      if (this.counter) this.counter.textContent = `${index + 1} / ${this.slides.length}`;
      this.slides.forEach((slide, i) => {
        if (i === index) return;
        slide.querySelectorAll('video').forEach((v) => v.pause());
        slide.querySelectorAll('iframe[src*="youtube"]').forEach((f) => f.contentWindow && f.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*'));
        slide.querySelectorAll('iframe[src*="vimeo"]').forEach((f) => f.contentWindow && f.contentWindow.postMessage('{"method":"pause"}', '*'));
      });
    }

    openLightbox(index, opener) {
      const lightbox = document.getElementById(this.dataset.lightbox);
      if (!lightbox) return;
      lightbox.open(opener);
      const img = lightbox.querySelectorAll('[data-lightbox-item]')[index];
      if (img) setTimeout(() => img.scrollIntoView({ block: 'start' }), 60);
    }
  }
  customElements.define('product-gallery', ProductGallery);

  /* 3D models: load Shopify's model viewer UI only when a product has models */
  const initModels = () => {
    const models = document.querySelectorAll('[data-model-viewer]:not([data-model-ready])');
    if (!models.length || !window.Shopify || !window.Shopify.loadFeatures) return;
    window.Shopify.loadFeatures([{
      name: 'model-viewer-ui',
      version: '1.0',
      onLoad: (errors) => {
        if (errors) return;
        models.forEach((el) => {
          el.dataset.modelReady = 'true';
          const viewer = el.querySelector('model-viewer');
          if (viewer && window.Shopify.ModelViewerUI) new window.Shopify.ModelViewerUI(viewer);
        });
      }
    }]);
  };

  /* ---------- Sticky add to cart ---------- */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      const target = document.getElementById(this.dataset.observe);
      this.form = document.getElementById(this.dataset.form);
      this.button = this.querySelector('[data-sticky-button]');
      if (!target || !this.form || !('IntersectionObserver' in window)) return;
      new IntersectionObserver(([entry]) => {
        const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.classList.toggle('is-visible', passed);
        this.setAttribute('aria-hidden', String(!passed));
        this.querySelectorAll('a, button').forEach((el) => { el.tabIndex = passed ? 0 : -1; });
      }).observe(target);
      this.button.addEventListener('click', () => {
        if (this.button.getAttribute('aria-disabled') === 'true') {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
        if (this.form.requestSubmit) this.form.requestSubmit();
        else this.form.querySelector('[type="submit"]').click();
      });
      const section = this.closest('[data-product-section]') || document;
      section.addEventListener('variant:change', (event) => {
        const variant = event.detail.variant;
        const price = this.querySelector('[data-sticky-price]');
        const title = this.querySelector('[data-sticky-variant]');
        if (price && variant) price.innerHTML = variant.price_html;
        if (title) title.textContent = variant ? variant.title : '';
        const label = this.button.querySelector('[data-add-label]') || this.button;
        if (!variant || !variant.available) {
          this.button.setAttribute('aria-disabled', 'true');
          label.textContent = variant ? strings.soldOut : strings.unavailable;
        } else {
          this.button.removeAttribute('aria-disabled');
          label.textContent = strings.addToCart;
        }
      });
    }
  }
  customElements.define('sticky-atc', StickyAtc);

  /* ---------- Slideshow (hero) ---------- */
  class SlideshowComponent extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-slide-dot]'));
      this.pauseBtn = this.querySelector('[data-slideshow-pause]');
      this.index = 0;
      this.interval = (parseInt(this.dataset.speed, 10) || 6) * 1000;
      this.autoplay = this.dataset.autoplay === 'true' && this.slides.length > 1 && !prefersReducedMotion.matches;
      if (this.slides.length < 2) return;

      this.dots.forEach((dot, i) => dot.addEventListener('click', () => { this.goTo(i); this.restart(); }));
      this.querySelectorAll('[data-slide-prev]').forEach((b) => b.addEventListener('click', () => { this.goTo(this.index - 1); this.restart(); }));
      this.querySelectorAll('[data-slide-next]').forEach((b) => b.addEventListener('click', () => { this.goTo(this.index + 1); this.restart(); }));
      if (this.pauseBtn) {
        this.pauseBtn.hidden = !this.autoplay;
        this.pauseBtn.addEventListener('click', () => { this.userPaused = !this.userPaused; this.userPaused ? this.stop() : this.start(); this.updatePauseButton(); });
      }
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => { if (!this.userPaused) this.start(); });
      this.addEventListener('focusin', () => this.stop());
      this.addEventListener('focusout', (e) => { if (!this.contains(e.relatedTarget) && !this.userPaused) this.start(); });

      // Swipe support
      let startX = null;
      this.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
      this.addEventListener('touchend', (e) => {
        if (startX === null) return;
        const dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) > 50) { this.goTo(this.index + (dx < 0 ? 1 : -1)); this.restart(); }
        startX = null;
      });

      document.addEventListener('shopify:block:select', (e) => {
        const i = this.slides.indexOf(e.target);
        if (i > -1) { this.userPaused = true; this.stop(); this.goTo(i); }
      });
      document.addEventListener('shopify:block:deselect', () => { this.userPaused = false; this.start(); });
      this.start();
    }

    disconnectedCallback() { this.stop(); }

    goTo(i) {
      const index = (i + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, n) => {
        const active = n === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.querySelectorAll('a, button').forEach((el) => { el.tabIndex = active ? 0 : -1; });
      });
      this.dots.forEach((dot, n) => dot.setAttribute('aria-current', String(n === index)));
      this.index = index;
    }

    start() {
      if (!this.autoplay || this.userPaused) return;
      this.stop();
      this.timer = setInterval(() => this.goTo(this.index + 1), this.interval);
    }
    stop() { clearInterval(this.timer); }
    restart() { if (!this.userPaused) this.start(); }
    updatePauseButton() {
      if (!this.pauseBtn) return;
      this.pauseBtn.setAttribute('aria-pressed', String(!!this.userPaused));
      this.pauseBtn.querySelector('[data-icon-pause]').hidden = !!this.userPaused;
      this.pauseBtn.querySelector('[data-icon-play]').hidden = !this.userPaused;
    }
  }
  customElements.define('slideshow-component', SlideshowComponent);

  /* ---------- Announcement bar rotation ---------- */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.messages = Array.from(this.querySelectorAll('[data-announcement]'));
      this.index = 0;
      if (this.messages.length < 2) return;
      this.querySelectorAll('[data-announcement-prev]').forEach((b) => b.addEventListener('click', () => { this.show(this.index - 1); this.restart(); }));
      this.querySelectorAll('[data-announcement-next]').forEach((b) => b.addEventListener('click', () => { this.show(this.index + 1); this.restart(); }));
      this.addEventListener('mouseenter', () => clearInterval(this.timer));
      this.addEventListener('mouseleave', () => this.restart());
      this.addEventListener('focusin', () => clearInterval(this.timer));
      document.addEventListener('shopify:block:select', (e) => {
        const i = this.messages.indexOf(e.target);
        if (i > -1) { clearInterval(this.timer); this.show(i); }
      });
      this.restart();
    }
    show(i) {
      this.index = (i + this.messages.length) % this.messages.length;
      this.messages.forEach((m, n) => {
        m.classList.toggle('is-active', n === this.index);
        m.setAttribute('aria-hidden', String(n !== this.index));
      });
    }
    restart() {
      clearInterval(this.timer);
      if (prefersReducedMotion.matches || this.dataset.autoplay !== 'true') return;
      this.timer = setInterval(() => this.show(this.index + 1), (parseInt(this.dataset.speed, 10) || 5) * 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
  }
  customElements.define('announcement-bar', AnnouncementBar);

  /* ---------- Horizontal product slider controls ---------- */
  class SliderComponent extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.track = this.querySelector('[data-slider-track]');
      this.prev = this.querySelector('[data-slider-prev]');
      this.next = this.querySelector('[data-slider-next]');
      if (!this.track) return;
      const step = (dir) => {
        const item = this.track.firstElementChild;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        const itemWidth = item ? item.getBoundingClientRect().width + gap : this.track.clientWidth;
        const perPage = Math.max(1, Math.floor((this.track.clientWidth + gap) / itemWidth));
        this.track.scrollBy({ left: dir * itemWidth * perPage, behavior: prefersReducedMotion.matches ? 'auto' : 'smooth' });
      };
      if (this.prev) this.prev.addEventListener('click', () => step(-1));
      if (this.next) this.next.addEventListener('click', () => step(1));
      this.track.addEventListener('scroll', debounce(() => this.update(), 60), { passive: true });
      window.addEventListener('resize', debounce(() => this.update(), 150));
      this.update();
    }
    update() {
      if (!this.track) return;
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.track.scrollLeft >= max;
      const controls = this.querySelector('.slider-controls');
      if (controls) controls.hidden = max <= 0;
    }
  }
  customElements.define('slider-component', SliderComponent);

  /* ---------- Tabs ---------- */
  class TabsComponent extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i));
        tab.addEventListener('keydown', (e) => {
          let next = null;
          if (e.key === 'ArrowRight') next = (i + 1) % this.tabs.length;
          if (e.key === 'ArrowLeft') next = (i - 1 + this.tabs.length) % this.tabs.length;
          if (e.key === 'Home') next = 0;
          if (e.key === 'End') next = this.tabs.length - 1;
          if (next !== null) { e.preventDefault(); this.select(next); this.tabs[next].focus(); }
        });
      });
      document.addEventListener('shopify:block:select', (e) => {
        const i = this.tabs.findIndex((t) => t.getAttribute('aria-controls') === (e.target.id || ''));
        if (i > -1) this.select(i);
      });
    }
    select(index) {
      this.tabs.forEach((tab, i) => {
        const selected = i === index;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
        const panel = document.getElementById(tab.getAttribute('aria-controls'));
        if (panel) {
          panel.classList.toggle('is-active', selected);
          panel.hidden = !selected;
          if (selected) panel.querySelectorAll('slider-component').forEach((s) => s.update && s.update());
        }
      });
    }
  }
  customElements.define('tabs-component', TabsComponent);

  /* ---------- Quick view & quick add ---------- */
  document.addEventListener('click', async (event) => {
    const trigger = event.target.closest('[data-quick-view]');
    if (!trigger) return;
    const modal = document.getElementById('QuickView');
    if (!modal) return;
    event.preventDefault();
    const content = modal.querySelector('[data-modal-content]');
    content.innerHTML = '<div class="loading-block"><span class="spinner" aria-hidden="true"></span><span class="visually-hidden">' + (strings.loading || 'Loading') + '</span></div>';
    modal.open(trigger);
    try {
      const url = trigger.dataset.quickView;
      const response = await fetch(`${url}${url.includes('?') ? '&' : '?'}section_id=quick-view`);
      if (!response.ok) throw new Error(response.status);
      content.innerHTML = sectionInnerHTML(await response.text(), '.shopify-section');
      if (window.Shopify && window.Shopify.PaymentButton) window.Shopify.PaymentButton.init();
      const focusTarget = content.querySelector('h2, [tabindex="-1"]');
      if (focusTarget) { focusTarget.setAttribute('tabindex', '-1'); focusTarget.focus({ preventScroll: true }); }
    } catch (error) {
      content.innerHTML = `<p class="form-message form-message--error">${strings.cartError}</p><p><a class="btn" href="${trigger.dataset.quickView}">${trigger.dataset.fallbackLabel || 'View product'}</a></p>`;
    }
  });

  /* Card swatches: preview the variant's image without leaving the grid */
  const swatchPreview = (swatch) => {
    const card = swatch.closest('.card');
    if (!card) return;
    const img = card.querySelector('.card__image--primary');
    if (img && swatch.dataset.image) {
      img.srcset = swatch.dataset.srcset || '';
      img.src = swatch.dataset.image;
    }
    card.querySelectorAll('.swatch.is-active').forEach((s) => s.classList.remove('is-active'));
    swatch.classList.add('is-active');
    if (swatch.dataset.variantUrl) {
      card.querySelectorAll('.card__link, .card__media-link').forEach((a) => { a.href = swatch.dataset.variantUrl; });
    }
  };
  document.addEventListener('click', (event) => {
    const swatch = event.target.closest('.card__swatches .swatch');
    if (swatch) { event.preventDefault(); swatchPreview(swatch); }
  });
  document.addEventListener('mouseover', (event) => {
    if (!canHover.matches) return;
    const swatch = event.target.closest && event.target.closest('.card__swatches .swatch');
    if (swatch) swatchPreview(swatch);
  });

  /* ---------- Product recommendations ---------- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this._loaded) return;
      const load = () => {
        this._loaded = true;
        fetch(this.dataset.url)
          .then((r) => r.text())
          .then((text) => {
            const doc = parseHTML(text);
            const fresh = doc.querySelector('product-recommendations');
            if (fresh && fresh.querySelector('.card')) {
              this.innerHTML = fresh.innerHTML;
              this.classList.add('is-loaded');
            } else {
              const section = this.closest('.shopify-section');
              if (section && !window.Shopify?.designMode) section.hidden = true;
            }
          })
          .catch(() => {});
      };
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => {
          if (entries.some((e) => e.isIntersecting)) { io.disconnect(); load(); }
        }, { rootMargin: '0px 0px 400px 0px' });
        io.observe(this.closest('.shopify-section') || this);
      } else load();
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  /* ---------- Recently viewed (stored in this browser only) ---------- */
  const RECENT_KEY = 'theme:recently-viewed';
  const readRecent = () => {
    try { return JSON.parse(localStorage.getItem(RECENT_KEY)) || []; } catch (e) { return []; }
  };
  const trackProduct = () => {
    const el = document.querySelector('[data-track-product]');
    if (!el) return;
    const handle = el.dataset.trackProduct;
    const list = [handle, ...readRecent().filter((h) => h !== handle)].slice(0, 16);
    try { localStorage.setItem(RECENT_KEY, JSON.stringify(list)); } catch (e) { /* storage unavailable */ }
  };

  class RecentlyViewed extends HTMLElement {
    async connectedCallback() {
      if (this._loaded) return;
      this._loaded = true;
      const current = this.dataset.currentHandle;
      const limit = parseInt(this.dataset.limit, 10) || 4;
      const handles = readRecent().filter((h) => h && h !== current).slice(0, limit);
      const grid = this.querySelector('[data-recent-grid]');
      if (!handles.length || !grid) return;
      const cards = await Promise.all(handles.map(async (handle) => {
        try {
          const response = await fetch(withRoot(`/products/${encodeURIComponent(handle)}?section_id=product-card-render`));
          if (!response.ok) return null;
          const card = parseHTML(await response.text()).querySelector('[data-card-item]');
          return card ? card.outerHTML : null;
        } catch (e) { return null; }
      }));
      const html = cards.filter(Boolean).join('');
      if (!html) return;
      grid.innerHTML = html;
      this.classList.add('has-items');
      const slider = this.querySelector('slider-component');
      if (slider && slider.update) slider.update();
    }
  }
  customElements.define('recently-viewed', RecentlyViewed);

  /* ---------- Video embeds (load player on demand) ---------- */
  class VideoEmbed extends HTMLElement {
    connectedCallback() {
      const button = this.querySelector('[data-video-play]');
      if (!button || this._bound) return;
      this._bound = true;
      button.addEventListener('click', () => {
        const template = this.querySelector('template');
        if (!template) return;
        const content = template.content.cloneNode(true);
        button.replaceWith(content);
        const media = this.querySelector('video, iframe');
        if (media && media.tagName === 'VIDEO') media.play().catch(() => {});
        if (media) media.focus();
      });
    }
  }
  customElements.define('video-embed', VideoEmbed);

  /* ---------- Localization selectors ---------- */
  class LocalizationForm extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', (event) => {
        if (event.target.matches('select')) this.querySelector('form').submit();
      });
    }
  }
  customElements.define('localization-form', LocalizationForm);

  /* ---------- Reveal on scroll ---------- */
  const initReveal = () => {
    if (!document.body.classList.contains('has-reveal')) return;
    const els = document.querySelectorAll('.reveal:not(.is-revealed)');
    if (!('IntersectionObserver' in window) || prefersReducedMotion.matches || (window.Shopify && window.Shopify.designMode)) {
      els.forEach((el) => el.classList.add('is-revealed'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -60px 0px', threshold: 0.05 });
    els.forEach((el) => {
      if (el.getBoundingClientRect().top < window.innerHeight) el.classList.add('is-revealed');
      else io.observe(el);
    });
  };

  /* ---------- Boot ---------- */
  const boot = () => {
    initReveal();
    initModels();
    trackProduct();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  document.addEventListener('shopify:section:load', () => { initReveal(); initModels(); });
})();
class CountrySelector extends HTMLElement {
  connectedCallback() {
    const details = this.querySelector('details');
    if (!details || this._bound) return;
    this._bound = true;
    this._onDocumentClick = (event) => {
      if (details.open && !this.contains(event.target)) details.open = false;
    };
    document.addEventListener('click', this._onDocumentClick);
    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !details.open) return;
      details.open = false;
      details.querySelector('summary').focus();
    });
  }
  disconnectedCallback() {
    document.removeEventListener('click', this._onDocumentClick);
    this._bound = false;
  }
}
customElements.define('country-selector', CountrySelector);